import { GRAY_700, GRAY_900, YELLOW_100, YELLOW_300, YELLOW_700 } from 'constants/styling/theme'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import Box from '@mui/material/Box'
import { ReactNode } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

/** The sx every icon passed into the card's leading chip uses, shared so all cards render one icon size/color */
export const measurementInfoCardIconSx = { fontSize: 20, color: YELLOW_700 }

/**
 * @interface MeasurementInfoCardProps Input properties of one info card
 */
interface MeasurementInfoCardProps {
  /** Icon displayed in the leading chip */
  icon: ReactNode
  /** Card title */
  title: string
  /** Muted hint closing the card */
  hint?: string
  /** Main card content */
  children: ReactNode
}

/**
 * @component
 * One measurement info card with a leading icon chip, a title, the main content and an optional muted
 * closing hint (Mila's review-step design, ISSUE-2189/ISSUE-3041). Consumed by MeasurementReviewInfo
 * (technician call / delivery time / cancellation policy) and by the Cost Calculation Notice in
 * ProductListing.
 *
 * @example
 * <MeasurementInfoCard icon={<PhoneInTalkOutlinedIcon sx={measurementInfoCardIconSx} />} title="Title">
 *   <Typography>Content</Typography>
 * </MeasurementInfoCard>
 */
export const MeasurementInfoCard: React.FC<MeasurementInfoCardProps> = ({ icon, title, hint, children }) => (
  <BorderBoxWrapper
    gap={2}
    padding={1}
    display="flex"
    elevation="none"
    borderColor={YELLOW_300}
    backgroundColor={YELLOW_100}
    sx={{ alignItems: 'flex-start' }}
  >

    <Box
      display="flex"
      padding={1.2}
      borderRadius=".6rem"
      sx={{ backgroundColor: YELLOW_300, color: YELLOW_700 }}
    >
      {icon}
    </Box>

    <Stack gap={1} flex={1}>

      <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
        {title}
      </Typography>

      {children}

      {!!hint &&
        <Typography variant="text-sm" color={GRAY_700}>
          {hint}
        </Typography>
      }

    </Stack>

  </BorderBoxWrapper>
)
